import { useEffect, useState } from "react";

import Icon from "./Icon";
import T, { GetLang } from "./translations/Translations";

import { grades } from "@const/Grades";

import { Student, StudentImage, StudentOe } from "@/types/GlobalState";

interface MarqueeProps {
    student: Student | StudentOe;
    studentImageList: StudentImage[];
}

/**
 * @name Marquee
 */
const Marquee = ({ student, studentImageList }: MarqueeProps): React.JSX.Element => {
    const [studentImage, setStudentImage] = useState<StudentImage | null>(null);
    const [studentName, setStudentName] = useState<React.ReactNode | null>(null);
    const lang = GetLang();

    const resolvedStudent: Student = "studentDto" in student ? (student.studentDto as Student) : student;
    const { address, firstName, gradeName, lastName, middleName, schoolName, studentNumber } = resolvedStudent;

    const newStudentStr = T({ tKey: "Marquee_NewStudent", langStr: lang });
    const notAvailableStr = T({ tKey: "Marquee_NotAvailable", langStr: lang });

    /**
     * Set the selected student's image if there is one
     */
    useEffect(() => {
        if (student && studentImageList) {
            const tmpImage = studentImageList.find(
                (obj) => Number.parseInt(obj.studentNumber, 10) === Number.parseInt(resolvedStudent.studentNumber, 10)
            );

            if (tmpImage) {
                setStudentImage(tmpImage);
            } else {
                setStudentImage(null);
            }
        }
    }, [resolvedStudent?.studentNumber, student, studentImageList]);

    /**
     * Set the student name string
     */
    useEffect(() => {
        if (student) {
            const fullName = [firstName, middleName, lastName].filter(Boolean).join(" ");
            const idStr = studentNumber || newStudentStr;
            setStudentName(
                <>
                    {fullName} - ({idStr})
                </>
            );
        }
    }, [firstName, lastName, middleName, newStudentStr, student, studentNumber]);

    return (
        <div className="marquee-container">
            <div>
                {studentImage?.imageUrl ? (
                    <div className="image-container">
                        <img alt={firstName} src={studentImage.imageUrl} />
                    </div>
                ) : (
                    <div className="no-image-container">
                        <Icon className="pb-3" fill="#19608F" height={140} iconName="NO_STUDENT_IMAGE" width={140} />
                    </div>
                )}
            </div>
            <div className="info-card">
                <div className="name">{studentName}</div>
                <hr />
                <div className="info-grid">
                    <div className="label">
                        {/* Grade: */}
                        {T({ tKey: "Marquee_Grade", langStr: lang })}:
                    </div>
                    <div className="info">{grades(gradeName)}</div>
                    <div className="label">
                        {/* School: */}
                        {T({ tKey: "Marquee_School", langStr: lang })}:
                    </div>
                    <div className="info">{schoolName || notAvailableStr}</div>
                    <div className="label">
                        {/* Address: */}
                        {T({ tKey: "Marquee_Address", langStr: lang })}:
                    </div>
                    <div className="info">
                        {address ? (
                            <>
                                <div>{address.addressLine1}</div>
                                {address.addressLine2 && <div>{address.addressLine2}</div>}
                                <div>
                                    {address.city ? `${address.city}, ` : ""}
                                    {address.state} {address.zip}
                                </div>
                            </>
                        ) : (
                            <div>{notAvailableStr}</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Marquee;
